import React from 'react';
import { Link } from 'react-router-dom';
import { useTransition } from '@react-spring/web';

import debounce from 'debounce';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight';
import { faArrowUp } from '@fortawesome/pro-solid-svg-icons/faArrowUp';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import { faFaceHeadBandage } from '@fortawesome/pro-solid-svg-icons/faFaceHeadBandage';
import { faHeart } from '@fortawesome/pro-solid-svg-icons/faHeart';
import { faHelicopter } from '@fortawesome/pro-solid-svg-icons/faHelicopter';
import { faHospital } from '@fortawesome/pro-solid-svg-icons/faHospital';
import { faMicrophoneLines } from '@fortawesome/pro-solid-svg-icons/faMicrophoneLines';
import { faMoonStars } from '@fortawesome/pro-solid-svg-icons/faMoonStars';
import { faPerson } from '@fortawesome/pro-solid-svg-icons/faPerson';
import { faSignalSlash } from '@fortawesome/pro-solid-svg-icons/faSignalSlash';
import { faSun } from '@fortawesome/pro-solid-svg-icons/faSun';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import screenshotGuideDark from '@screenshots/webp/dark/FieldGuide.webp';
import screenshotGuide2Dark from '@screenshots/webp/dark/FieldGuide2.webp';
import screenshotH2TDark from '@screenshots/webp/dark/HeadToToe.webp';
import screenshotPDFDark from '@screenshots/webp/dark/PDF.webp';
import screenshotSceneSurveyDark from '@screenshots/webp/dark/SceneSurvey.webp';
import screenshotScribeDark from '@screenshots/webp/dark/Scribe-transcript.webp';
import screenshotAppToAppDark from '@screenshots/webp/dark/ShareAppToApp.webp';
import screenshotMessagesDark from '@screenshots/webp/dark/ShareComposer.webp';
import screenshotHeroDark from '@screenshots/webp/dark/SOAP-live-combined-card.webp';
import screenshotSpanishDark from '@screenshots/webp/dark/Spanish.webp';
import screenshotVitalsRecordingDark from '@screenshots/webp/dark/Vitals-recording-active.webp';
import screenshotVitalsHistoryDark from '@screenshots/webp/dark/VitalsHistory.webp';
import screenshotGuide from '@screenshots/webp/light/FieldGuide.webp';
import screenshotGuide2 from '@screenshots/webp/light/FieldGuide2.webp';
import screenshotH2T from '@screenshots/webp/light/HeadToToe.webp';
import screenshotPDF from '@screenshots/webp/light/PDF.webp';
import screenshotSceneSurvey from '@screenshots/webp/light/SceneSurvey.webp';
import screenshotScribe from '@screenshots/webp/light/Scribe-transcript.webp';
import screenshotAppToApp from '@screenshots/webp/light/ShareAppToApp.webp';
import screenshotMessages from '@screenshots/webp/light/ShareComposer.webp';
import screenshotHero from '@screenshots/webp/light/SOAP-live-combined-card.webp';
import screenshotSpanish from '@screenshots/webp/light/Spanish.webp';
import screenshotVitalsRecording from '@screenshots/webp/light/Vitals-recording-active.webp';
import screenshotVitalsHistory from '@screenshots/webp/light/VitalsHistory.webp';

import {
  AnimatedColumn,
  AnimatedColumnChild
} from '@wildidea/components/AnimatedColumn/AnimatedColumn';
import { useAnimatedRouteState } from '@wildidea/components/AnimatedRoutes/AnimatedRoutes';
import { Button } from '@wildidea/components/Button/Button';
import { Card } from '@wildidea/components/Card/Card';
import { useBodyClass } from '@wildidea/components/hooks/useBodyClass';
import { useConst } from '@wildidea/components/hooks/useConst';
import { useDeclarativeRef } from '@wildidea/components/hooks/useDeclarativeRef';
import { useMergedRefs } from '@wildidea/components/hooks/useMergedRefs';
import { useOsScheme } from '@wildidea/components/hooks/useOsScheme';
import { useThemeAnimations, useThemeBreakpoints } from '@wildidea/components/Theme/Theme';
import { scrollToTop } from '@wildidea/components/utils/scroll';

import { GetTheAppCTA } from '@components/GetTheAppCTA.js';
import { PhoneFrame } from '@components/PhoneFrame.js';
import { useIntersectionObserver } from '@components/useIntersectionObserver.js';

import { ScreenshotCarousel } from '../../../components/ScreenshotCarousel.js';

import logo from './img/logo.png';

import * as css from './HomeRoute.module.css';

const HookSection: React.FC<{
  scheme: 'light' | 'dark';
}> = ({ scheme }) => {
  const { phase, onTransitionEnd } = useAnimatedRouteState();
  const active = phase === 'active' || phase === 'enter';
  const { fromBelow } = useThemeAnimations('Common');
  const splashTransition = useTransition(active, { ...fromBelow, onRest: onTransitionEnd });
  const breakpoints = useThemeBreakpoints();

  const description = (
    <>
      <Card color="paper1" className={css.descriptionCard} elevated>
        <div className={css.descriptionInner}>
          <p className={css.lead}>Knowledge is ultralight</p>
          <p>
            <strong className={css.appName}>Rescue Ally</strong> is an offline-first wilderness
            medicine app built for those who act when resources are far away.
          </p>
          <p>
            The first of its kind for professional rescuers, outdoor guides, everyday adventurers
            and grassroots community responders, Rescue Ally guides you through assessment,
            treatment, and documentation. The result is more focus on the patient, better
            documentation, faster coordination and a better chance for every patient.
          </p>
          <div className={css.ctaHeroBox}>
            <GetTheAppCTA className={css.cta} />
          </div>
        </div>
      </Card>
      {!breakpoints.lg ? <FooterNav className={css.headerNav} /> : null}
    </>
  );

  return (
    <section className={css.hookSection}>
      <div className={css.hookWrapper}>
        <div className={css.hookLeft}>
          <div className={css.logoWrapper}>
            <img src={logo} className={css.logo} />
            <GetTheAppCTA hideLabel allPlatforms className={css.heroStores} />
          </div>

          {breakpoints.lg ? description : null}
          {/* Desktop only: the site links live in the dead space under the
              description card rather than in a full-width row at the bottom of
              the hero. They sit INSIDE hookLeft (40em) so footerNav's existing
              flex-wrap breaks them into rows; the card itself deliberately
              overflows this column (width: 100% + 30em) to run under the
              phone, so the nav must not inherit that width. */}
          {breakpoints.lg ? <FooterNav className={css.heroNav} /> : null}

          {/* {breakpoints.lg ? (
            <>
              <Card color="paper2" className={css.headingCard}>
                <div className={css.headingCardInner}>
                  <h1>
                    <span>Rescue</span> <span>Ally</span>
                  </h1>
                </div>
              </Card>
              {description}
            </>
          ) : (
            <Card color="paper2" className={css.headingCard}>
              <div className={css.headingCardInner}>
                <h1>
                  <span>Rescue</span> <span>Ally</span>
                </h1>
              </div>
              <div className={css.presents}>Field Medicine App</div>
            </Card>
          )} */}
        </div>
        <div className={css.hookRight}>
          <div className={css.hookPhoneWrapper}>
            {splashTransition((style, active) =>
              active ? (
                <PhoneFrame
                  style={style}
                  className={[css.phone, css.hookPhone].filter(Boolean).join(' ')}
                >
                  <img
                    src={scheme === 'dark' ? screenshotHeroDark : screenshotHero}
                    className={css.screenshot}
                  />
                </PhoneFrame>
              ) : null
            )}
          </div>
        </div>
      </div>

      {!breakpoints.lg ? description : null}
    </section>
  );
};

const FooterNav: React.FC<{ className?: string }> = ({ className }) => (
  <nav className={[css.footerNav, className].filter(Boolean).join(' ')}>
    <Link to="/pricing" className={css.link}>
      Pricing
    </Link>
    <Link to="/teams" className={css.link}>
      Teams
    </Link>
    <Link to="/about" className={css.link}>
      About Us
    </Link>
    <Link to="/news" className={css.link}>
      News
    </Link>
    <Link to="/ambassadors" className={css.link}>
      Ambassadors
    </Link>
    <a href="https://app.rescueally.org/faq" className={css.link}>
      FAQ
    </a>
    <Link to="/privacy" className={css.link}>
      Privacy Policy
    </Link>
    <Link to="/security" className={css.link}>
      Security
    </Link>
    <Link to="/baa" className={css.link}>
      HIPAA BAA
    </Link>
    <Link to="/terms" className={css.link}>
      Terms of Service
    </Link>
    <Link to="/sms" className={css.link}>
      SMS Terms
    </Link>
  </nav>
);

const HomeRoute: React.FC<{}> = () => {
  const osScheme = useOsScheme();
  const [chosenScheme, setChosenScheme] = React.useState<typeof osScheme | null>(null);
  const scheme = chosenScheme || osScheme;
  const schemeRef = useDeclarativeRef(scheme);

  const flipScheme = useConst(() => {
    const scheme = schemeRef.current;
    if (scheme === 'light') {
      setChosenScheme('dark');
    } else {
      setChosenScheme('light');
    }
  });

  useBodyClass([css.body, chosenScheme && `rescueally-${chosenScheme}`].filter(Boolean).join(' '));
  const [carousel1Intersecting, carousel1ObserverRef] = useIntersectionObserver({ threshold: 0.7 });
  const carousel1IntersectingRef = useDeclarativeRef(carousel1Intersecting);
  const carousel1ElementRef = React.useRef<HTMLDivElement>(null);
  const carousel1Ref = useMergedRefs(carousel1ObserverRef, carousel1ElementRef);

  const [carousel2Intersecting, carousel2ObserverRef] = useIntersectionObserver({ threshold: 0.7 });
  const carousel2IntersectingRef = useDeclarativeRef(carousel2Intersecting);
  const carousel2ElementRef = React.useRef<HTMLDivElement>(null);
  const carousel2Ref = useMergedRefs(carousel2ObserverRef, carousel2ElementRef);

  const maybeSnapToCarousel = useConst(
    debounce(() => {
      if (carousel1IntersectingRef.current) {
        if (carousel1ElementRef.current) {
          scrollToTop(carousel1ElementRef.current);
        }
      } else if (carousel2IntersectingRef.current) {
        if (carousel2ElementRef.current) {
          scrollToTop(carousel2ElementRef.current);
        }
      }
    }, 100)
  );

  return (
    <div
      className={css.layout}
      onScroll={() => {
        maybeSnapToCarousel();
      }}
    >
      <React.Suspense fallback={null}>
        <HookSection scheme={scheme} />
      </React.Suspense>

      <div className={css.content}>
        <section className={[css.contentRow, css.lifecycleCardRow].filter(Boolean).join(' ')}>
          <Card
            color="paper2"
            className={css.lifecycleCard}
            header={<strong>Rescue Ally is built for the moments that matter.</strong>}
            elevated
          >
            <div className={css.lifecycleRow}>
              <div className={css.lifecycleItem}>
                <FontAwesomeIcon icon={faExclamationTriangle} size="2xl" />
                <span>Scene Survey</span>
              </div>
              <div className={css.lifecycleItem}>
                <FontAwesomeIcon icon={faHeart} size="2xl" />
                <span>Primary Survey</span>
              </div>
              <div className={css.lifecycleItem}>
                <FontAwesomeIcon icon={faPerson} size="2xl" />
                <span>Secondary Survey</span>
              </div>
              <div className={css.lifecycleItem}>
                <FontAwesomeIcon icon={faFaceHeadBandage} size="2xl" />
                <span>Treatment</span>
              </div>
              <div className={css.lifecycleItem}>
                <FontAwesomeIcon icon={faHelicopter} size="2xl" />
                <span>Evacuation</span>
              </div>
              <div className={css.lifecycleItem}>
                <FontAwesomeIcon icon={faHospital} size="2xl" />
                <span>Transfer</span>
              </div>
            </div>
            <Card
              color="error"
              figure={<FontAwesomeIcon icon={faSignalSlash} size="lg" />}
              className={css.offlineFirst}
            >
              <strong>No bars, no problem.</strong> <span>Always ready, no signal required.</span>
            </Card>
          </Card>
        </section>

        {/* Scribe launch (2.0). Deliberately BRIEF and high on the page: the
            claim is unusual enough that the job here is to make it land and
            send people to /demo/scribe, not to explain the whole feature. */}
        <section className={css.scribeSection}>
          <div className={css.scribeCopy}>
            <div className={css.scribeBadge}>
              <FontAwesomeIcon icon={faMicrophoneLines} />
              <span>New in Rescue Ally 2.0</span>
            </div>
            <h2>
              Focus on the patient.
              <br />
              <span className={css.scribeEmphasis}>Scribe writes it down.</span>
            </h2>
            <p>
              Start a Recording and record a voice description of your observations, or interview
              your patient normally. Scribe will transcribe in real-time and fill the SOAP Note for
              you - fully offline.
            </p>
            {/* A PLAIN ANCHOR, not <Link>: /demo/scribe is a prerendered static
                page (prerender.config.js → marketing/demo/scribe.html), not an
                SPA route, so a client-side navigation is swallowed by the
                catch-all route and redirected home. Same reason
                StrapRouteRedirect uses window.location.assign. */}
            <a href="/demo/scribe" className={css.scribeLink}>
              See how it works <FontAwesomeIcon icon={faArrowRight} />
            </a>
            <div className={css.scribeStores}>
              <span className={css.scribeStoresLabel}>Or get the app:</span>
              <GetTheAppCTA hideLabel allPlatforms className={css.scribeStoreButtons} />
            </div>
          </div>
          <div className={css.scribePhoneWrapper}>
            <PhoneFrame className={[css.phone, css.scribePhone].filter(Boolean).join(' ')}>
              <img
                src={scheme === 'dark' ? screenshotScribeDark : screenshotScribe}
                className={css.screenshot}
                alt="Scribe transcribing a spoken patient report into the SOAP note"
              />
            </PhoneFrame>
          </div>
        </section>

        <React.Suspense fallback={null}>
          <ScreenshotCarousel
            ref={carousel1Ref}
            header={
              <h2>
                <span className={css.nowrap}>Why you'll use it </span>{' '}
                <span className={css.nowrap}>every time:</span>
              </h2>
            }
            className={css.carousel}
            items={[
              {
                phonePlacement: 'start',
                screenshot: scheme === 'dark' ? screenshotH2TDark : screenshotH2T,
                alt: 'Screenshot of the Rescue Ally Wilderness Medicine/WFR app, showing a wire mannequin of the human body that allows users to select injured areas and record observations',
                annotations: [
                  {
                    header: 'Document anywhere, no signal needed',
                    className: `${css.headToToe_annotation1} ${css.annotation}`,
                    children: (
                      <p>
                        Capture patient care in the most remote places with fully offline
                        capability.
                      </p>
                    )
                  },
                  {
                    header: null,
                    className: `${css.headToToe_annotation2} ${css.annotation}`,
                    children: <p>Rescuers record as much or as little detail as necessary.</p>
                  }
                ]
              },
              {
                phonePlacement: 'end',
                screenshot:
                  scheme === 'dark' ? screenshotVitalsRecordingDark : screenshotVitalsRecording,
                alt: 'Screenshot of the Rescue Ally Wilderness Medicine/WFR app, showing the Real Time Vitals view, with recordings from a pulse oximeter',
                annotations: [
                  {
                    header: 'Track vitals in real time via Bluetooth',
                    className: `${css.realtime_annotation1} ${css.annotation}`,
                    children: (
                      <p>
                        Rescue Ally integrates with common HR broadcast devices like Garmin and
                        COROS watches or pulse oximeters.
                      </p>
                    )
                  }
                ]
              },
              {
                phonePlacement: 'start',
                screenshot: scheme === 'dark' ? screenshotSceneSurveyDark : screenshotSceneSurvey,
                alt: 'Screenshot of the Rescue Ally Wilderness Medicine/WFR app, showing the Scene Survey view, with a step-by-step flow through through the scene safety process',
                annotations: [
                  {
                    header: 'Stay on track when moments count',
                    className: `${css.outline_annotation1} ${css.annotation}`,
                    children: (
                      <p>
                        Step-by-step prompts guide you through the full assessment so nothing slips
                        in high-stress situations.
                      </p>
                    )
                  }
                ]
              },
              {
                phonePlacement: 'end',
                screenshot: scheme === 'dark' ? screenshotMessagesDark : screenshotMessages,
                alt: 'Screenshot of the Rescue Ally Wilderness Medicine/WFR app message composer, assembling a structured report from the SOAP note into a text message ready to send by SMS or commercial satellite communicator',
                annotations: [
                  {
                    header: 'Send what matters most',
                    className: `${css.messages_annotation1} ${css.annotation}`,
                    children: (
                      <p>
                        Messages are automatically prepared from your SOAP note for faster help and
                        better coordination.
                      </p>
                    )
                  },
                  {
                    header: null,
                    className: `${css.messages_annotation2} ${css.annotation}`,
                    children: <p>Message length is checked for satellite messaging devices.</p>
                  }
                ]
              },
              {
                phonePlacement: 'start',
                screenshot: scheme === 'dark' ? screenshotSpanishDark : screenshotSpanish,
                alt: 'Screenshot of the Rescue Ally Wilderness Medicine/WFR app, showing the app features Spanish translations',
                annotations: [
                  {
                    header: 'Bridge the language gap',
                    className: `${css.spanish_annotation1} ${css.annotation}`,
                    children: (
                      <p>
                        The interface is available in English and Spanish, supporting responders and
                        the communities they serve.
                      </p>
                    )
                  }
                ]
              }
            ]}
          />
        </React.Suspense>

        <section className={css.trustStatement}>
          <div className={css.trustStatementInner}>
            <h2>Guidance you can trust</h2>
            <p>
              <span className={css.appName}>Rescue Ally</span> is produced by{' '}
              <strong>Backcountry Pulse</strong>, a world-class wilderness medicine education
              organization based in the Rocky Mountains of Colorado.
            </p>
            <p>
              Backcountry Pulse was built on the belief that lifesaving skills belong to everyone.
              We design and teach <strong>evidence-based wilderness medicine</strong> with an
              emphasis on compassion for <strong>patients AND rescuers.</strong> We believe rescuers
              come from and belong to every community - and that communities are made stronger when
              people learn lifesaving and leadership skills.
            </p>
            <p>That's why Rescue Ally is designed to be used by people at all training levels:</p>
            <ul>
              <li>WFA (Wilderness First Aid)</li>
              <li>WFR (Wilderness First Responder)</li>
              <li>WEMT (Wilderness EMT)</li>
              <li>Any non-wilderness medical professional (EMT, Paramedic, RN, MD)</li>
              <li>
                Even without training, the step-by-step assessment walkthrough and comprehensive
                field medicine guide would be useful - but we always encourage people to{' '}
                <a
                  href="https://www.backcountrypulse.org/"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className={css.link}
                >
                  get the proper education.
                </a>
              </li>
            </ul>
            <p>
              <strong>
                Every feature of Rescue Ally is forged in real rescue, shaped by cold nights, long
                carries, and the shared goal of building capable responders and resilient
                communities.
              </strong>
            </p>
            <p>
              We are committed to complete data privacy for you and your patients. The app is{' '}
              <strong>HIPAA compliant</strong> with a clear and simple{' '}
              <Link to="/privacy">Privacy Policy</Link>. Paient data is stored securely and will
              never be sold, period.
            </p>
          </div>
        </section>

        <React.Suspense fallback={null}>
          <ScreenshotCarousel
            ref={carousel2Ref}
            header={<h2>The features that back you up:</h2>}
            className={css.carousel}
            items={[
              {
                phonePlacement: 'start',
                screenshot: scheme === 'dark' ? screenshotGuideDark : screenshotGuide,
                alt: "Screenshot of the Rescue Ally Wilderness Medicine/WFR app, showing the comprehensive wilderness medicine field guide, specifically a section containing instructions on how to find a patient's pulse",
                annotations: [
                  {
                    header: 'Knowledge at your fingertips',
                    className: `${css.guide_annotation1} ${css.annotation}`,
                    children: (
                      <p>
                        Backcountry Pulse’s{' '}
                        <a
                          href="https://backcountrypulse.org/bookstore/the-little-big-book-of-wilderness-medicine"
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                        >
                          trusted wilderness medicine publication,
                        </a>{' '}
                        offline and field-ready, putting essential knowledge in your hands when it
                        matters most.
                      </p>
                    )
                  }
                ]
              },
              {
                phonePlacement: 'end',
                screenshot:
                  scheme === 'dark' ? screenshotVitalsHistoryDark : screenshotVitalsHistory,
                alt: 'Screenshot of the Rescue Ally Wilderness Medicine/WFR app, showing the Vitals History view, which allows rescuers to track trends in vitals over time',
                annotations: [
                  {
                    header: 'Track vitals over time',
                    className: `${css.vitalsHistory_annotation1} ${css.annotation}`,
                    children: (
                      <p>
                        Recording vitals over a period of time reveals trends in the patient's
                        condition.
                      </p>
                    )
                  },
                  {
                    header: 'Never miss a critical detail',
                    className: `${css.vitalsHistory_annotation2} ${css.annotation}`,
                    children: (
                      <p>
                        Programmable reminders for vitals, reassessments and rescuer check-ins to
                        ensure nothing is missed in the field.
                      </p>
                    )
                  }
                ]
              },
              {
                phonePlacement: 'start',
                screenshot: scheme === 'dark' ? screenshotAppToAppDark : screenshotAppToApp,
                alt: 'Screenshot of the Rescue Ally Wilderness Medicine/WFR app, showing the App to App Transfer screen, which allows rescuers to share data with other Rescue Ally users via animated QR codes',
                annotations: [
                  {
                    header: 'Share with other rescuers in the field, without service',
                    className: `${css.appToApp_annotation1} ${css.annotation}`,
                    children: (
                      <p>
                        App-to-App transfer shares the entire patient assessment with another Rescue
                        Ally user.
                      </p>
                    )
                  }
                ]
              },
              {
                phonePlacement: 'end',
                screenshot: scheme === 'dark' ? screenshotPDFDark : screenshotPDF,
                alt: 'Screenshot of the Rescue Ally Wilderness Medicine/WFR app, showing the PDF Export, which allows rescuers to collect all patient information into a single document for patient transfer',
                annotations: [
                  {
                    header: 'Seamless handoffs every time',
                    className: `${css.pdf_annotation1} ${css.annotation}`,
                    children: (
                      <p>
                        PDF SOAP Notes turn your field documentation into professional records that
                        can be shared when transferring patients.
                      </p>
                    )
                  }
                ]
              },
              {
                phonePlacement: 'start',
                screenshot: scheme === 'dark' ? screenshotGuide2Dark : screenshotGuide2,
                alt: 'Screenshot of the Rescue Ally Wilderness Medicine/WFR app, showing the comprehensive wilderness medicine field guide, specifically a section containing detailed instructions on building improvised rescue litters',
                annotations: [
                  {
                    header: 'Built on hard-earned knowledge',
                    className: `${css.guide2_annotation1} ${css.annotation}`,
                    children: (
                      <p>
                        Created by wilderness responders and outdoor professionals who have faced
                        these challenges themselves.
                      </p>
                    )
                  },
                  {
                    header: 'Confidence you can trust',
                    className: `${css.guide2_annotation2} ${css.annotation}`,
                    children: (
                      <p>
                        Built on internationally accepted, evidence-based wilderness medicine
                        guidelines.
                      </p>
                    )
                  }
                ]
              }
            ]}
          />
        </React.Suspense>

        <React.Suspense fallback={null}>
          <LightDarkModeCard scheme={scheme} chosenScheme={chosenScheme} flipScheme={flipScheme} />
        </React.Suspense>
        <footer>
          <FooterNav />
          <div className={css.copyright}>© 2026 Rescue Ally LLC</div>
        </footer>
      </div>
    </div>
  );
};

const LightDarkModeCard: React.FC<{
  scheme: 'light' | 'dark';
  chosenScheme: 'light' | 'dark' | null;
  flipScheme: () => void;
}> = ({ scheme, chosenScheme, flipScheme }) => (
  <Card
    color="paper2"
    className={css.lightDarkModeCard}
    figure={<FontAwesomeIcon icon={scheme === 'light' ? faSun : faMoonStars} size="2xl" />}
  >
    <AnimatedColumn>
      <AnimatedColumnChild key="heading">
        <p suppressHydrationWarning>
          {scheme === 'light' ? 'Low light?' : 'Too dark?'} No problem. The app is available in both
          light and dark modes.
        </p>
      </AnimatedColumnChild>
      <AnimatedColumnChild key="button">
        <Button appearance="outline" color="paper1" onClick={flipScheme} suppressHydrationWarning>
          {scheme === 'light' ? 'Try Dark Mode' : scheme === 'dark' ? 'Try Light Mode' : null}
        </Button>
      </AnimatedColumnChild>
      {chosenScheme ? (
        <AnimatedColumnChild key="hint">
          <p suppressHydrationWarning>
            <FontAwesomeIcon icon={faArrowUp} /> Scroll back up for a{' '}
            {scheme === 'light' ? 'daytime' : 'nighttime'} experience.
          </p>
        </AnimatedColumnChild>
      ) : null}
    </AnimatedColumn>
  </Card>
);

export default HomeRoute;
